import semver from 'semver'
import parseCooldown from '../lib/parseCooldown'
import * as versionUtil from '../lib/version-util'
import type { CooldownFunction } from '../types/CooldownFunction'
import type { Index } from '../types/IndexType'
import type { Maybe } from '../types/Maybe'
import type { Options } from '../types/Options'
import type { Packument } from '../types/Packument'
import type { Version } from '../types/Version'

/**
 * @param versionResult  Available version
 * @param options     Options
 * @returns         True if deprecated versions are allowed or the version is not deprecated
 */
export function allowDeprecatedOrIsNotDeprecated(versionResult: Partial<Packument>, options: Options): boolean {
  return options.deprecated || !versionResult.deprecated
}

/**
 * @param versionResult  Available version
 * @param options     Options
 * @returns         True if pre-releases are allowed or the version is not a pre-release
 */
export function allowPreOrIsNotPre(versionResult: Partial<Packument>, options: Options): boolean {
  if (options.pre) return true
  return !versionResult.version || !versionUtil.isPre(versionResult.version)
}

/**
 * Returns true if the node engine requirement is satisfied or not specified for a given package version.
 *
 * @param versionResult     Version object returned by packument.
 * @param nodeEngineVersion The value of engines.node in the package file.
 * @returns                 True if the node engine requirement is satisfied or not specified.
 */
export function satisfiesNodeEngine(versionResult: Partial<Packument>, nodeEngineVersion: Maybe<string>): boolean {
  if (!nodeEngineVersion) return true
  const minVersion = semver.minVersion(nodeEngineVersion)?.version
  if (!minVersion) return true
  const versionNodeEngine: string | undefined = versionResult?.engines?.node
  return !versionNodeEngine || semver.satisfies(minVersion, versionNodeEngine)
}

/**
 * Returns true if the peer dependencies requirement is satisfied or not specified for a given package version.
 *
 * @param versionResult     Version object returned by packument.
 * @param peerDependencies  The list of peer dependencies.
 * @returns                 True if the peer dependencies are satisfied or not specified.
 */
export function satisfiesPeerDependencies(versionResult: Partial<Packument>, peerDependencies: Index<Index<Version>>) {
  if (!peerDependencies) return true
  return Object.values(peerDependencies).every(
    peers =>
      peers[versionResult.name!] === undefined || semver.satisfies(versionResult.version!, peers[versionResult.name!]),
  )
}

/**
 * Determines if a package version satisfies the specified cooldown period.
 *
 * @param packageName - Package name used when cooldown is a predicate.
 * @param version - The version string.
 * @param versionTimeData - The publish time for the specific version.
 * @param cooldownDaysOrPredicateFn - Cooldown period in days or package-name predicate.
 * If not specified or invalid, the function returns true.
 */
export const satisfiesCooldownPeriod = (
  packageName: string,
  version: Maybe<string>,
  versionTimeData: Maybe<string>,
  cooldownDaysOrPredicateFn: Maybe<number | string> | Maybe<CooldownFunction>,
): boolean => {
  if (!version) return false

  if (!cooldownDaysOrPredicateFn) return true
  if (!versionTimeData) return false

  const versionReleaseDate = new Date(versionTimeData)
  const DAY_AS_MS = 86400000 // milliseconds in a day
  const rawCooldown =
    typeof cooldownDaysOrPredicateFn === 'function'
      ? (cooldownDaysOrPredicateFn(packageName ?? '') ?? 0) // null → 0 days = no cooldown
      : cooldownDaysOrPredicateFn
  const cooldownDays = typeof rawCooldown === 'string' ? (parseCooldown(rawCooldown) ?? 0) : rawCooldown

  return Date.now() - versionReleaseDate.getTime() >= cooldownDays * DAY_AS_MS
}

/**
 * Returns a composite predicate that filters out deprecated, prerelease,
 * and node engine incompatibilities from version objects returns by packument.
 *
 * Note: this function does not filter cooldown.
 */
export function filterPredicate(options: Options) {
  const predicates: (((o: Partial<Packument>) => boolean) | null)[] = [
    o => allowDeprecatedOrIsNotDeprecated(o, options),
    o => allowPreOrIsNotPre(o, options),
    options.enginesNode ? o => satisfiesNodeEngine(o, options.nodeEngineVersion) : null,
    options.peerDependencies ? o => satisfiesPeerDependencies(o, options.peerDependencies!) : null,
  ]

  return (o: Partial<Packument>) => predicates.every(predicate => (predicate ? predicate(o) : true))
}
